import {
  BufferDataPointDTO,
  IIngredientDocAliasDTO,
  IIngredientDocBufferDataPoint,
  IIngredientDocCasNumberDTO,
  IIngredientDocInfo,
  IIngredientDocStockDTO,
  Ingredient,
  State,
} from 'business/models/Models';
import { getNewId, getRequiredFiledsError, isValidNumber } from 'utils/Global';
import { ChartData, NUM_OF_LABELS, STOCK_COLUMNS, infoRequiredFields } from './Constants';
import { RowExtraInfo, RowInfoType } from 'components/customComponents/Controls/RMTable';
import { IngredientDocumentData } from 'redux/reducers/ingredientDetailReducer';
import { BufferCalculationType } from 'business/BufferCalculationType';

export const getNewDefaultStock = (
  stocks: IIngredientDocStockDTO[],
  liquidClassId: number,
  defaultDispenseOrder: number,
) => {
  let stockNumber = 1;
  stocks.forEach((s) => {
    if (s.stockNumber >= stockNumber) {
      stockNumber = s.stockNumber + 1;
    }
  });

  return {
    id: getNewId(),
    stockNumber: stockNumber,
    active: true,
    stockConcentration: null,
    concentrationUnits: '',
    lowConcentration: null,
    highConcentration: null,
    liquidClassId: liquidClassId,
    isBuffer: false,
    stockPh: null,
    vendorName: '',
    vendorId: -1,
    vendorPartNumber: '',
    dispenseOrder: defaultDispenseOrder,
    isPrepared: false,
    state: State.New,
  } as IIngredientDocStockDTO;
};

export const getVisibleStockColumns = (
  hideIrrelevantColumns: boolean,
  hasBufferType: boolean,
  hasNonBufferType: boolean,
) => {
  if (!hideIrrelevantColumns) {
    return STOCK_COLUMNS;
  }

  const onlyBufferType = hasBufferType && !hasNonBufferType;
  const onlyNonBufferType = hasNonBufferType && !hasBufferType;

  return STOCK_COLUMNS.filter((column) => {
    if (column.accessor === 'nonBufferDefault' && onlyBufferType) {
      return false;
    }
    if (column.accessor === 'bufferDefault' && onlyNonBufferType) {
      return false;
    }
    return true;
  });
};

export const getTitrationChartValues = (pKa: number) => {
  let pH;
  const lowerPHLimit = 0.0;
  const higherPHLimit = 1.0;
  const totalPoints = 100;

  const pHDelta = (pKa + higherPHLimit - (pKa - lowerPHLimit)) / totalPoints;
  pH = pKa - lowerPHLimit;

  let ratio, difference;
  const values: ChartData[] = [];
  for (let i = 0; i < totalPoints; i++) {
    difference = pH - pKa;
    ratio = Math.pow(10, difference);
    ratio = 1 / ratio;
    values[i] = { x: roundedFloat(pH), y: roundedFloat(ratio) };
    pH += pHDelta;
  }
  return values;
};

export const getIngredient = (
  ingInfo: IIngredientDocInfo,
  bufferData: IIngredientDocBufferDataPoint[],
) => {
  const ing = new Ingredient();
  ing.id = ingInfo.id;
  ing.pKa = ingInfo.pKa;
  ing.bufferCalculationTypeId = ingInfo.bufferCalculationTypeId;
  ing.bufferDataPoint = bufferData.map((bd) => {
    const point = new BufferDataPointDTO();
    point.id = bd.id;
    point.ingredientId = bd.ingredientId;
    point.ph = bd.ph;
    point.ratio = bd.ratio;
    return point;
  });
  return ing;
};

export const getGridStepAt = (start, stop, step, gridSteps) => {
  if (start == stop) {
    return start;
  }
  if (gridSteps == 1) {
    return start;
  }
  if (step <= 0) {
    return start;
  }
  if (step >= gridSteps - 1) {
    return stop;
  }
  const num = (stop - start) / (gridSteps - 1);
  return parseFloat(start + step * num).toFixed(8);
};

export const getAxisLabels = (min, max, numOfLabels) => {
  const xDiff = (max - min) / numOfLabels;
  const labels = [min];
  for (let i = 0; i <= numOfLabels - 2; i++) {
    const val = roundedFloat(labels.at(labels.length - 1) + xDiff);
    labels.push(val);
  }
  labels.push(max);
  return labels;
};

export const roundedFloat = (val: number, fractionDigits = 2) => {
  if (val) {
    const numericVal = parseFloat(val + '');
    if (numericVal) {
      return parseFloat(numericVal.toFixed(fractionDigits));
    }
  }
  return val;
};

export const getLablesFromChartData = (chartData: ChartData[]) => {
  const labels = () => {
    if (chartData.length) {
      const xSeries = chartData.map((p) => p.x);
      const ySeries = chartData.map((p) => p.y);
      const minX = Math.min(...xSeries);
      const maxX = Math.max(...xSeries);
      const minY = Math.min(...ySeries);
      const maxY = Math.max(...ySeries);
      const x = getAxisLabels(minX, maxX, NUM_OF_LABELS);
      const y = getAxisLabels(minY, maxY, NUM_OF_LABELS);
      return { x, y };
    }
    return { x: [], y: [] };
  };
  return labels();
};

export const getStockError = (
  ingredient: IngredientDocumentData,
): { error: boolean; stockRowInfo: RowExtraInfo; msg: string } => {
  let hasError = false;
  const stockRowInfo: RowExtraInfo = {};
  let detailedError =
    'There are following errors in the Stocks table that must be fixed before it can be saved\n';
  const stocks = ingredient.stocks;
  stocks.forEach((stock, i) => {
    let msg = '';
    if (!stock.concentrationUnits) {
      msg = 'Concentration Unit is a required field.\n';
    } else if (!isValidNumber(stock.stockConcentration)) {
      msg = 'Stock Concentration is not a valid number.\n';
    } else if (!isValidNumber(stock.lowConcentration)) {
      msg = 'Low Concentration is not a valid number.\n';
    } else if (!isValidNumber(stock.highConcentration)) {
      msg = 'High Concentration is not a valid number.\n';
    } else if (
      parseFloat(stock.stockConcentration + '') < parseFloat(stock.lowConcentration + '')
    ) {
      msg = 'Low concentration must be less than stock concentration.\n';
    } else if (
      parseFloat(stock.stockConcentration + '') < parseFloat(stock.highConcentration + '')
    ) {
      msg = 'High concentration must be less than stock concentration.\n';
    } else if (
      parseFloat(stock.lowConcentration + '') > parseFloat(stock.highConcentration + '')
    ) {
      msg = 'Low concentration must be less than high concentration.\n';
    } else if (stock.dispenseOrder <= 0) {
      msg = 'Dispense Order must be grerater than or equal to 1.\n';
    } else if (stock.isBuffer && !stock.stockPh) {
      msg = 'pH is required for buffer stocks.\n';
    } else if (
      stock.isBuffer &&
      ingredient.info.bufferCalculationTypeId === BufferCalculationType.Interpolate.ID &&
      !ingredient.bufferDataPoint?.some((point) => point.ph >= stock.stockPh)
    ) {
      msg =
        'At least one buffer data point must be greater than or equal to Stock High pH.\n';
    }
    if (msg) {
      stockRowInfo[i] = { msg, type: RowInfoType.Error };
      detailedError += '- ' + msg;
      hasError = true;
    }
  });
  detailedError +=
    '\nErrors are indicated by error icon on the first column of the stock row.\nBring mouse over each indicator to see specific error message';
  return { error: hasError, stockRowInfo, msg: detailedError };
};

export const validateIngredient = (
  ingredient: IngredientDocumentData,
): {
  msg: string;
  stockRowInfo: RowExtraInfo;
  aliasRowInfo: RowExtraInfo;
  casNumberRowInfo: RowExtraInfo;
  bufferDataErrors: RowExtraInfo;
} => {
  const infoError = getRequiredFiledsError(ingredient.info, infoRequiredFields);
  const stocksError = getStockError(ingredient);
  const casNumberErrors = validateCasNumbers(ingredient.casNumbers);
  const aliasErrors = validateAliases(ingredient.aliases);
  const bufferDataErrors = validateBufferDataPoints(ingredient);
  let msg = '';
  msg = infoError ? infoError + '\n\n' : '';

  if (ingredient.info.shortName?.length > 0) {
    msg +=
      ingredient.info.shortName?.trim()?.length > 8
        ? '- Ingredient Short Name cannot contain more than 8 characters\n'
        : '';
  }
  if (ingredient.types.length <= 0) {
    msg += '- At least one type must be associated with the ingredient\n';
  }
  if (
    ingredient.types.find((t) => t.isBuffer) &&
    !ingredient.info.defaultBufferIngredientStockId
  ) {
    msg += '- At least one stock must be set as default buffer stock\n';
  }
  if (
    ingredient.types.find((t) => !t.isBuffer) &&
    !ingredient.info.defaultNonBufferIngredientStockId
  ) {
    msg += '- At least one stock must be set as default non-buffer stock\n';
  }
  if (Object.keys(aliasErrors).length) {
    msg += 'Aliases must be unique.\n';
  }
  if (Object.keys(casNumberErrors).length) {
    msg += 'CAS Numbers must be unique.\n';
  }
  if (Object.keys(bufferDataErrors).length) {
    msg += 'pH values must be unique in buffer data points.\n';
  }

  msg += stocksError.error ? '\n\n' + stocksError.msg + '\n\n' : '';

  return {
    msg,
    stockRowInfo: stocksError.stockRowInfo,
    aliasRowInfo: aliasErrors,
    casNumberRowInfo: casNumberErrors,
    bufferDataErrors: bufferDataErrors,
  };
};

const validateCasNumbers = (casNumbers: IIngredientDocCasNumberDTO[]): RowExtraInfo => {
  const casNumberErrors: RowExtraInfo = {};
  casNumbers.forEach((casNumber, i) => {
    const isDuplicate = casNumbers.some(
      (casNum) => casNum.number === casNumber.number && casNum.id !== casNumber.id,
    );
    if (isDuplicate) {
      casNumberErrors[i] = { msg: 'CAS Number must be unique.', type: RowInfoType.Error };
    }
  });
  return casNumberErrors;
};

const validateAliases = (aliases: IIngredientDocAliasDTO[]): RowExtraInfo => {
  const aliasErrors: RowExtraInfo = {};
  aliases.forEach((alias, i) => {
    const isDuplicate = aliases.some((a) => a.name === alias.name && a.id !== alias.id);
    if (isDuplicate) {
      aliasErrors[i] = { msg: 'Alias must be unique.', type: RowInfoType.Error };
    }
  });
  return aliasErrors;
};

const validateBufferDataPoints = (ingredient: IngredientDocumentData): RowExtraInfo => {
  const errors: RowExtraInfo = {};
  if (
    ingredient.types.find((t) => t.isBuffer) &&
    ingredient.info.bufferCalculationTypeId === BufferCalculationType.Interpolate.ID
  ) {
    const seen = new Set();
    ingredient.bufferDataPoint.forEach((item, index) => {
      if (seen.has(parseFloat(item.ph + ''))) {
        errors[index] = { msg: 'PH must be unique.', type: RowInfoType.Error };
      } else {
        seen.add(parseFloat(item.ph + ''));
      }
    });
  }
  return errors;
};
